// src/App.js
import React, { Suspense, useState, useRef, useEffect } from "react";
import { Navbar, Container, Button, ListGroup, Form } from "react-bootstrap";
import {
  FaSync,
  FaSignOutAlt,
  FaArrowLeft,
  FaBars,
  FaListAlt,
  FaBoxes,
  FaWarehouse,
  FaEye,
  FaClipboardCheck,
  FaUserShield,
  FaUsers,
  FaCogs,
  FaEdit,
  FaReceipt,
  FaThLarge,
} from "react-icons/fa";

import UpdateBanner from "./components/shared/UpdateBanner";

import { login as apiLogin } from "./api";
import { AlertProvider, useGlobalAlert } from "./components/shared/AlertContext";
import GlobalAlert from "./components/shared/GlobalAlert";
import { ConfirmDialog } from "./components/shared/ConfirmDialog";
import { useSessionHeartbeat } from "./utils/heart";

const Orders = React.lazy(() => import("./components/Orders"));
const Warehouse = React.lazy(() => import("./components/Warehouse"));
const SyncDashboard = React.lazy(() => import("./components/SyncDashboard"));
const SyncResults = React.lazy(() => import("./components/SyncResults"));
const Apografi = React.lazy(() => import("./components/Apografi"));
const AdminClients = React.lazy(() => import("./components/AdminClients"));
const AdminClientSettings = React.lazy(() => import("./components/AdminClientSettings"));
const AdminUsers = React.lazy(() => import("./components/AdminUsers"));
const AdminModules = React.lazy(() => import("./components/AdminModules"));
const ItemManagement = React.lazy(() => import("./components/ItemManagement"));
const AddCustomerForm = React.lazy(() => import("./components/AddCustomerForm"));
const Receipts = React.lazy(() => import("./components/Receipts"));
const PurOrders = React.lazy(() => import("./components/PurOrders"));
const EditOrders = React.lazy(() => import("./components/EditOrders"));
const AadeImports = React.lazy(() => import("./components/AadeImports"));
const AadeUsageAdminPage = React.lazy(() => import("./components/AadeUsageAdminPage"));
const SettingsPage = React.lazy(() => import("./components/SettingsPage"));

// --- BrightID-style LoginScreen component ---
function LoginScreen({ login, setLogin, doLogin }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        background: "#d3dee0ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div>
        {/* Logo + Title */}
        <div style={{ textAlign: "center", marginBottom: 26 }}>
          <img
            src="/android-chrome-512x512.png"
            alt="Logo"
            style={{ height: 50, marginBottom: 10 }}
          />
          <div
            style={{
              fontWeight: 700,
              fontSize: "2rem",
              color: "#fff",
              letterSpacing: 0.2,
              fontFamily: "system-ui, sans-serif",
            }}
          >
            <span style={{ color: "#28a745" }}>Info</span>
            <span style={{ color: "#007bff" }}>Con</span>
          </div>
        </div>

        <div
          style={{
            minWidth: 350,
            maxWidth: 390,
            borderRadius: 12,
            boxShadow: "0 6px 32px 0 #0003",
            background: "#fff",
          }}
        >
          <div style={{ padding: 32 }}>
            <h3 className="mb-4 text-center" style={{ fontWeight: 600 }}>
              Είσοδος
            </h3>
            <Form onSubmit={doLogin}>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Όνομα Χρήστη"
                  value={login.user}
                  onChange={(e) => setLogin((l) => ({ ...l, user: e.target.value }))}
                  autoFocus
                  required
                  style={{ fontSize: 15 }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Control
                  type="password"
                  placeholder="Κωδικός"
                  value={login.pass}
                  onChange={(e) => setLogin((l) => ({ ...l, pass: e.target.value }))}
                  required
                  style={{ fontSize: 15 }}
                />
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                className="w-100"
                style={{ fontWeight: 600, fontSize: 17 }}
              >
                Login
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin menu
const ADMIN_ITEMS = [
  { key: "admin_clients", label: "Clients", icon: <FaUserShield /> },
  { key: "admin_users", label: "Users", icon: <FaUsers /> },
  { key: "admin_modules", label: "Modules", icon: <FaThLarge /> },
  { key: "admin_aade_usage", label: "Χρήση AADE", icon: <FaReceipt /> },
];

// JWT decode helper
function getJwtExpiry(token) {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload && payload.exp) return payload.exp * 1000; // ms
  } catch (e) {}
  return null;
}

function AppInner() {
  // Auth info
  const savedToken = window.localStorage.getItem("token");
  const savedRole = window.localStorage.getItem("role") || "";
  const savedModulesRaw = JSON.parse(window.localStorage.getItem("modules") || "[]");
  const savedModules = Array.isArray(savedModulesRaw)
    ? savedModulesRaw.map((m) => (typeof m === "string" ? m : m.name))
    : [];
  const savedClientName = window.localStorage.getItem("clientName") || "";
  const savedUsername = window.localStorage.getItem("username") || "";

  const [login, setLogin] = useState({
    user: "",
    pass: "",
    token: savedToken || "",
    role: savedRole,
    modules: savedModules,
    clientName: savedClientName,
    username: savedUsername,
    due_date: window.localStorage.getItem("due_date") || "",
  });

  // Navigation & history
  const [active, setActive] = useState("orders");
  const [history, setHistory] = useState([]);
  const [showSidebar, setShowSidebar] = useState(() => window.innerWidth >= 992);
  const [hasUnsavedModuleWork, setHasUnsavedModuleWork] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [adminClientSettingsId, setAdminClientSettingsId] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const onResize = () => {
      const nextIsMobile = window.innerWidth < 992;
      setIsMobile(nextIsMobile);
      setShowSidebar((current) => (nextIsMobile ? false : current));
    };
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!hasUnsavedModuleWork) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedModuleWork]);

  // Unified alerts via context
  const { showAlert } = useGlobalAlert();

  // ===========================
  // SW update banner state
  // ===========================
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener("infocon-sw-update", handler);
    return () => window.removeEventListener("infocon-sw-update", handler);
  }, []);

  const handleRefreshToNewVersion = () => {
    const reg = window.__SW_REG__;
    if (!reg || !reg.waiting) {
      window.location.reload();
      return;
    }

    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });

    reg.waiting.postMessage("SKIP_WAITING");
  };

  // ✅ Banner visible ONLY on sync + settings
  const shouldShowUpdateBanner =
    !!login.token &&
    updateAvailable &&
    (active === "sync" || active === "settings");

  // --- Auto-logout on JWT expiration ---
  const logoutTimeoutRef = useRef(null);
  useEffect(() => {
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);

    if (login.token) {
      const expiry = getJwtExpiry(login.token);
      if (expiry) {
        const msLeft = expiry - Date.now();
        if (msLeft > 0) {
          logoutTimeoutRef.current = setTimeout(() => {
            handleLogout();
            showAlert({
              message: "Η συνεδρία σας έληξε. Παρακαλώ συνδεθείτε ξανά.",
              variant: "warning",
            });
          }, msLeft);
        } else {
          handleLogout();
          showAlert({
            message: "Η συνεδρία σας έληξε. Παρακαλώ συνδεθείτε ξανά.",
            variant: "warning",
          });
        }
      }
    }
    return () => {
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    };
    // Recalculate the logout timer only when the token changes.
  }, [login.token]);

  useEffect(() => {
    if (login.due_date) {
      const today = new Date();
      const expiry = new Date(login.due_date);
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysLeft = Math.ceil((expiry - today) / msPerDay);

      if (daysLeft <= 30 && daysLeft >= 0) {
        const lastDismiss = localStorage.getItem("expiryWarningDismissed");
        const todayStr = today.toISOString().slice(0, 10);
        if (lastDismiss === todayStr) return;

        showAlert({
          message: `Η συνδρομή σας λήγει σε ${daysLeft} ${
            daysLeft === 1 ? "μέρα" : "μέρες"
          }.`,
          variant: daysLeft <= 7 ? "danger" : "warning",
          dismissible: true,
          onClose: () => {
            localStorage.setItem("expiryWarningDismissed", todayStr);
          },
        });
      }
    }
  }, [login.due_date, showAlert]);

  // Helpers
  const hasModule = (mod) => login.modules && login.modules.includes(mod);
  const isAdmin = login.role === "admin" || login.role === "superadmin";

  const completeNavigation = (newKey) => {
    if (newKey !== active) {
      setHistory((h) => [...h, active]);
      setActive(newKey);
    }
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  // Sidebar navigation handler with history
  const handleSetActive = (newKey) => {
    if (newKey !== active && hasUnsavedModuleWork) {
      setPendingNavigation({ type: "active", key: newKey });
      return;
    }

    completeNavigation(newKey);
  };

  const openAdminClientSettings = (clientId = null) => {
    setAdminClientSettingsId(clientId);

    if (active !== "admin_client_settings") {
      setHistory((h) => [...h, active]);
      setActive("admin_client_settings");
    }
  };

  const closeAdminClientSettings = () => {
    setAdminClientSettingsId(null);
    setActive("admin_clients");
  };

  // Back button handler
  const handleBack = () => {
    if (hasUnsavedModuleWork) {
      setPendingNavigation({ type: "back" });
      return;
    }

    if (isMobile) {
      setShowSidebar(false);
    }

    if (active === "admin_client_settings") {
      closeAdminClientSettings();
      return;
    }

    if (history.length > 0) {
      setActive(history[history.length - 1]);
      setHistory(history.slice(0, -1));
    }
  };

  const confirmPendingNavigation = () => {
    const navigation = pendingNavigation;

    setPendingNavigation(null);
    setHasUnsavedModuleWork(false);

    if (!navigation) return;

    if (navigation.type === "back") {
      if (isMobile) {
        setShowSidebar(false);
      }

      if (active === "admin_client_settings") {
        closeAdminClientSettings();
        return;
      }

      if (history.length > 0) {
        setActive(history[history.length - 1]);
        setHistory(history.slice(0, -1));
      }
      return;
    }

    if (navigation.type === "active") {
      completeNavigation(navigation.key);
      return;
    }

    if (navigation.type === "logout") {
      handleLogout();
    }
  };

  // --- Auth logic
  const doLogin = async (e) => {
    e.preventDefault();
    try {
      const data = await apiLogin(login.user, login.pass);
      if (data.token) {
        const modules = Array.isArray(data.modules)
          ? data.modules.map((m) => (typeof m === "string" ? m : m.name))
          : [];
        setLogin((l) => ({
          ...l,
          token: data.token,
          role: data.role || "",
          modules,
          clientName: data.clientName || "",
          username: data.username || "",
          due_date: data.due_date || "",
        }));
        window.localStorage.setItem("token", data.token);
        window.localStorage.setItem("role", data.role || "");
        window.localStorage.setItem("modules", JSON.stringify(modules));
        window.localStorage.setItem("clientName", data.clientName || "");
        window.localStorage.setItem("username", data.username || "");
        window.localStorage.setItem("due_date", data.due_date || "");
      } else {
        showAlert({ message: data.error || "Login failed", variant: "danger" });
      }
    } catch (err) {
      let message = "Login failed.";
      if (err.response && err.response.data && err.response.data.error) {
        message = err.response.data.error;
      } else if (err.message) {
        message = err.message;
      }
      showAlert({ message, variant: "danger" });
    }
  };

  const handleLogout = () => {
    setLogin({ user: "", pass: "", token: "", role: "", modules: [], clientName: "", username: "" });
    window.localStorage.clear();
    setUpdateAvailable(false);
    showAlert({
      message:
        "Αποσυνδεθήκατε επειδή η σύνδεσή σας χρησιμοποιήθηκε σε άλλη συσκευή ή έληξε η περίοδος λειτουργίας.",
      variant: "warning",
    });
  };

  const requestLogout = () => {
    if (hasUnsavedModuleWork) {
      setPendingNavigation({ type: "logout" });
      return;
    }

    handleLogout();
  };

  useSessionHeartbeat(handleLogout, 900000, login.token);

  if (!login.token) {
    return (
      <>
        <GlobalAlert />
        <LoginScreen login={login} setLogin={setLogin} doLogin={doLogin} />
      </>
    );
  }

  const sidebarNav = [
    hasModule("orders") && { key: "orders", label: "Παραγγελίες", icon: <FaListAlt /> },
    hasModule("purorders") && { key: "purorders", label: "Αγορές", icon: <FaClipboardCheck /> },
    hasModule("editorders") && { key: "editorders", label: "Επεξεργασία Παραγγελιών", icon: <FaEdit /> },
    hasModule("warehouse") && { key: "warehouse", label: "Αποθήκη", icon: <FaWarehouse /> },
    hasModule("apografi") && { key: "apografi", label: "Απογραφή", icon: <FaClipboardCheck /> },
    hasModule("Receipts") && { key: "receipts", label: "Εισπράξεις", icon: <FaReceipt /> },
    hasModule("AADE Imports") && { key: "aade_imports", label: "Εισαγωγή myDATA", icon: <FaReceipt /> },
    hasModule("Item Management") && { key: "item_management", label: "Διαχείριση Είδους", icon: <FaBoxes /> },

    { key: "add_customer", label: "Προσθήκη Πελάτη", icon: <FaUsers /> },
    { key: "sync", label: "Συγχρονισμός", icon: <FaSync /> },
    isAdmin && { key: "syncresults", label: "Sync Viewer", icon: <FaEye /> },
  ].filter(Boolean);

  const sidebarAdmin = isAdmin ? ADMIN_ITEMS : [];
  const sidebarWidth = 240;

  return (
    <>
      <GlobalAlert />
      <ConfirmDialog
        show={!!pendingNavigation}
        title="Έξοδος χωρίς αποθήκευση;"
        message="Υπάρχουν αλλαγές σε εξέλιξη. Αν συνεχίσετε, θα χαθεί η τρέχουσα φόρμα."
        cancelText="Παραμονή"
        confirmText="Έξοδος"
        confirmVariant="warning"
        onCancel={() => setPendingNavigation(null)}
        onConfirm={confirmPendingNavigation}
      />

      <Navbar
          bg="primary"
          variant="dark"
          fixed="top"
          className="mb-4 shadow"
          style={{ minHeight: 56, padding: 0, zIndex: 1100 }}
        >
          <div
            style={{
              width: "100vw",
              minHeight: 56,
              position: "relative",
              margin: 0,
              padding: "0 10px",
              display: "grid",
              gridTemplateColumns: "96px 1fr 96px",
              alignItems: "center",
            }}
          >
            <div
              style={{
                justifySelf: "start",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Button
                variant="outline-light"
                onClick={handleBack}
                style={{
                  boxShadow: "none",
                  border: "1px solid #fff4",
                  borderRadius: 8,
                  width: 38,
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  padding: 0,
                }}
                tabIndex={0}
              >
                <FaArrowLeft />
              </Button>

              <Button
                variant="outline-light"
                onClick={() => setShowSidebar((v) => !v)}
                className="menu-burger-btn"
                style={{
                  boxShadow: "none",
                  border: "1px solid #fff4",
                  borderRadius: 8,
                  width: 38,
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  padding: 0,
                }}
                tabIndex={0}
              >
                <FaBars />
              </Button>
            </div>

            <div
              style={{
                justifySelf: "center",
                fontWeight: 700,
                fontSize: "1.4rem",
                letterSpacing: 0.5,
                userSelect: "none",
              }}
            >
              <span style={{ color: "#0eeb41ff" }}>Info</span>
              <span style={{ color: "#ffffff" }}>Con</span>
            </div>

            <div />
          </div>
        </Navbar>

      <Container
          fluid
          style={{
            paddingTop: 70,
            paddingLeft: 0,
            paddingRight: 0,
            overflowX: "hidden",
          }}
        >
        <div style={{ minHeight: "calc(100vh - 70px)" }}>
          {showSidebar && (
            <div
              style={{
                position: "fixed",
                top: 56,
                left: 0,
                width: sidebarWidth,
                height: "calc(100vh - 56px)",
                background: "#fff",
                borderRight: "1px solid #ddd",
                zIndex: 1040,
                overflowY: "auto",
              }}
            >
              <div
                className="d-flex align-items-center justify-content-between px-2"
                style={{
                  height: 48,
                  borderBottom: "1px solid #ddd",
                  fontSize: 18,
                  fontWeight: 500,
                }}
              >
                <span>Menu</span>
                <Button
                  variant="link"
                  className="p-0 text-muted"
                  onClick={() => setShowSidebar(false)}
                  style={{
                    fontSize: 26,
                    lineHeight: 1,
                    textDecoration: "none",
                  }}
                >
                  ×
                </Button>
              </div>

              <div className="p-0">
              <ListGroup variant="flush">
                {sidebarNav.map((item) => (
                  <ListGroup.Item key={item.key} action active={active === item.key} onClick={() => handleSetActive(item.key)} className="d-flex align-items-center" style={{ cursor: "pointer" }}>
                    <span className="me-2">{item.icon}</span>
                    {item.label}
                  </ListGroup.Item>
                ))}

                {sidebarAdmin.length > 0 && (
                  <>
                    <div className="px-3 pt-3 text-muted fw-bold small">Admin</div>
                    {sidebarAdmin.map((item) => (
                      <ListGroup.Item key={item.key} action active={active === item.key} onClick={() => handleSetActive(item.key)} className="d-flex align-items-center" style={{ cursor: "pointer" }}>
                        <span className="me-2">{item.icon}</span>
                        {item.label}
                      </ListGroup.Item>
                    ))}
                  </>
                )}

                <div className="px-3 pt-4 pb-2 border-top text-muted small">
                  <div>
                    <FaUsers className="me-1" /> <span>User:</span>{" "}
                    <span style={{ color: "#222", fontWeight: 500 }}>{login.username}</span>
                  </div>
                  <div>
                    <FaUserShield className="me-1" /> <span>Client:</span>{" "}
                    <span style={{ color: "#222", fontWeight: 500 }}>{login.clientName}</span>
                  </div>
                </div>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100 mb-2 d-flex align-items-center justify-content-center"
                  style={{ fontWeight: 500, fontSize: 15, borderRadius: 8, minWidth: 0, gap: 10, padding: "7px 14px" }}
                  onClick={() => handleSetActive("settings")}
                >
                  <FaCogs style={{ fontSize: 18, display: "inline-block" }} />
                  <span style={{ display: "inline-block" }}>Ρυθμίσεις</span>
                </Button>

                <Button
                  variant="danger"
                  size="sm"
                  className="w-100 d-flex align-items-center justify-content-center"
                  style={{ fontWeight: 500, fontSize: 15, borderRadius: 8, minWidth: 0, gap: 10, padding: "7px 14px" }}
                  onClick={requestLogout}
                >
                  <FaSignOutAlt style={{ fontSize: 18, display: "inline-block" }} />
                  <span style={{ display: "inline-block" }}>Αποσύνδεση</span>
                </Button>
              </ListGroup>
            </div>
          </div> 
        )}

          <div
                className="px-3 py-3"
                style={{
                  background: "#ffffff",
                  minHeight: 600,
                  marginLeft: showSidebar && !isMobile ? sidebarWidth : 0,
                  width:
                    showSidebar && !isMobile
                      ? `calc(100% - ${sidebarWidth}px)`
                      : "100%",
                  maxWidth:
                    showSidebar && !isMobile
                      ? `calc(100% - ${sidebarWidth}px)`
                      : "100%",
                  transition: "margin-left 0.2s ease, width 0.2s ease",
                  overflowX: "auto",
                }}
              >
            <Suspense fallback={<div className="p-3 text-muted">Φόρτωση...</div>}>
              {active === "orders" && hasModule("orders") && (
                <Orders login={login} onUnsavedChange={setHasUnsavedModuleWork} />
              )}
              {active === "purorders" && hasModule("purorders") && (
                <PurOrders login={login} onUnsavedChange={setHasUnsavedModuleWork} />
              )}
              {active === "editorders" && hasModule("editorders") && <EditOrders login={login} />}
              {active === "warehouse" && hasModule("warehouse") && (
                <Warehouse login={login} onUnsavedChange={setHasUnsavedModuleWork} />
              )}
              {active === "apografi" && hasModule("apografi") && (
                <Apografi login={login} onUnsavedChange={setHasUnsavedModuleWork} />
              )}
              {active === "receipts" && hasModule("Receipts") && (
                <Receipts login={login} onUnsavedChange={setHasUnsavedModuleWork} />
              )}
              {active === "item_management" && hasModule("Item Management") && <ItemManagement login={login} />}
              {active === "add_customer" && <AddCustomerForm token={login.token} />}
              {active === "aade_imports" && hasModule("AADE Imports") && <AadeImports login={login} />}
              {active === "sync" && <SyncDashboard token={login.token} />}
              {active === "settings" && <SettingsPage />}
              {isAdmin && active === "syncresults" && <SyncResults token={login.token} />}
              {isAdmin && active === "admin_clients" && (
                <AdminClients
                  token={login.token}
                  onOpenClientSettings={openAdminClientSettings}
                />
              )}
              {isAdmin && active === "admin_client_settings" && (
                <AdminClientSettings
                  token={login.token}
                  clientId={adminClientSettingsId}
                  onBack={closeAdminClientSettings}
                  onSaved={closeAdminClientSettings}
                />
              )}
              {isAdmin && active === "admin_users" && <AdminUsers token={login.token} />}
              {isAdmin && active === "admin_modules" && <AdminModules token={login.token} />}
              {isAdmin && active === "admin_aade_usage" && (
                <AadeUsageAdminPage login={login} />
              )}
            </Suspense>
          </div>
        </div>
      </Container>

      {/* ✅ Update banner ONLY on Sync + Settings */}
      <UpdateBanner
        show={shouldShowUpdateBanner}
        onRefresh={handleRefreshToNewVersion}
        onDismiss={() => setUpdateAvailable(false)}
      />
    </>
  );
}

export default function App() {
  return (
    <AlertProvider>
      <AppInner />
    </AlertProvider>
  );
}
